import { z } from "zod";
import { COMBINED_KINDS } from "../competition/combined";
import { CRICKET_WICKETS_MAX } from "../competition/cricket";
import { MATCH_EXTRA_WORDS, MATCH_PERIOD_WORDS, MATCH_SHOOTOUT_WORDS } from "../competition/match-format";
import { LIFT_TYPES, MATCH_EVENT_ADDED_MAX, RESULT_HEIGHT_TRIES_PATTERN, ROUND_LABEL_MAX, ROUND_PARTS_MAX, ROUND_WORDS, SETS_BEST_OF_MAX, TEAM_STAT_CODES, type LiftType } from "../competition/results";
import { BOUT_DECISIONS } from "../competition/ranking";
import { JUDGED_PARTS_MAX } from "../competition/judged-rules";
import { IRM_CODES, OFFICIAL_ROLES } from "../competition/vocab";
import { STREAM_PLATFORMS } from "../competition/streams";

/**
 * The PUBLIC results feed — `GET /api/public/results-feed?games=sea|para`.
 *
 * Unauthenticated. The web portal's whole results section (schedule, live
 * centre, medal table, athletes) in ONE payload, the shape the portal's
 * simulator builds (`results-data.js` `hydrate()` is the consumer), so the
 * pages cannot tell the feed from the simulation. Every relation is an id;
 * the portal puts the object references back.
 *
 * Keys are snake_case and single letters (`g`, `t`, `e`, `v`…) on purpose:
 * they are the portal's own field names, and `ok()`'s snake_case pass leaves
 * them unchanged. No key is ever derived from data.
 *
 * The ten top-level keys are always present (lists `[]` for an empty
 * games; `featured_video` null while the games has none switched on). Inside them absent means absent: the feed never sends `null`,
 * `false` or `[]` for an optional field (the portal copies "the keys that
 * are set") — except
 * `athletes[].given` / `family` and `events[].label`, which are always
 * strings, and the positional `units[].src` array, whose `null` entry means
 * "this side has no feeder".
 *
 * Combined events (decathlon, heptathlon — one meet per discipline, each
 * with `config.combined`) are ONE medal unit, not one per discipline: the
 * event carries `combined`, `type: "p"`, `dec: 0`; its discipline units are
 * phase `R` and the last discipline is the `F` medal unit. A discipline
 * unit's rows are the overall standing after that discipline — `v` the
 * running total, `rank` the overall place (total, then the phase's tie
 * order, as the POINTS_SUM standings) — plus the discipline itself: `pts`
 * its points, `m` the mark in the UNIT's own `type` / `dec`, `dr` its place
 * in the discipline. So the medal unit's rows are the final standings, and
 * the portal's podium reads them unchanged.
 *
 * A match (SETS) also carries what OVR's match sheet records: `stats`, the
 * whole-match team statistics (home `a` vs away `b`; the sport's own codes,
 * `teamStatCodesFor`, in that order — football's possession and shots,
 * badminton's longest run, sepak takraw's aces and spikes), `events`, the
 * goals, cards and substitutions in match order, each with its player's
 * athlete id (football), and `gdur` / `mdur`, the games' and the match's
 * durations in minutes (badminton).
 *
 * A race (TIMED) carries its wind reading (`wind`, m/s; `wa` when it makes
 * the marks wind-assisted) and each row its reaction time (`rt`, seconds),
 * its splits (`sp`, the elapsed time at each split, the finish left out) and,
 * for a relay, its legs (`legs`, in leg order: the swimmer / runner, the
 * take-off and the leg time);
 * a jump or throw (ATTEMPTS) row carries the wind of its best mark (`w`,
 * `wa`) and its whole series — every attempt (`at`, by round) or, for the
 * high jump and pole vault, every height with its tries (`ht`); a
 * weightlifting (LIFTS) row its lifts (`lifts`).
 *
 * A team match (SETS) carries each side's line-up (`lu`: the players, who
 * started, the captain, the position) and any unit its officials (`off`:
 * role, printed name, seat; a coach or manager names their entrant) — user,
 * 2026-11-02: an official's NAME is public, as on the printed result sheet.
 *
 * An invalid result (DSQ, DNF, DNS…) is its IRM code, as the printed result
 * sheet shows it: a ranked row with `irm` has no `rank`, no `v` (a
 * disqualified swimmer's time is struck, never published), no split, record
 * or qualification, and comes after the ranked rows; a head-to-head's `res`
 * names it per side (`irm.a` / `irm.b`) beside the score as it stood — a
 * retirement at 11–6 in game 2 is `1–0` with `irm.b: "RET"`.
 *
 * Live video (0086, user 2026-11-03): a unit with a video link carries
 * `video` (`src` = the embed to iframe, from contracts `parseStreamUrl` — the
 * one parser; a stored link it no longer accepts is not sent), and
 * `featured_video` is the games' featured stream while it is on. The portal's
 * home page reads the small `GET /public/live-videos` instead, derived from
 * this same snapshot.
 *
 * Privacy: nothing here identifies a person beyond the printed name — and,
 * for an athlete with one, the card's TV photo (user, 2026-11-02; only the
 * `ph` flag is here, the picture is its own route). No date of birth, age, ID
 * document, contact, accreditation photo, bib, result source, medal-desk
 * note or proposal ever reaches this shape. Officials are a role and a
 * printed name only — no nation, no link to their accreditation.
 */

/** The `?games=` values, and the games row each one reads (`games.code`). */
export const RESULTS_FEED_GAMES = ["sea", "para"] as const;
export type ResultsFeedGames = (typeof RESULTS_FEED_GAMES)[number];

export const RESULTS_FEED_GAMES_CODES = { sea: "SEAG", para: "APG" } as const satisfies Record<ResultsFeedGames, string>;

/**
 * The eleven NOCs the portal knows (`results-data.js` NOCS). Its medal table
 * and `nocName()` THROW on any other code, so an entrant of another
 * organisation is dropped from the feed (with its results) — never sent.
 */
export const RESULTS_FEED_NOC_CODES = ["MAS", "THA", "VIE", "INA", "PHI", "SGP", "MYA", "CAM", "LAO", "BRU", "TLS"] as const;
export type ResultsFeedNocCode = (typeof RESULTS_FEED_NOC_CODES)[number];

/**
 * Stored IOC code → the portal's code. The countries seed stores Singapore as
 * SIN (its historical code); the portal knows SGP. An alias here, not a data
 * migration (decided 2026-10-19).
 */
export const RESULTS_FEED_NOC_ALIASES: Readonly<Record<string, ResultsFeedNocCode>> = { SIN: "SGP" };

/**
 * `sports.code` → the portal's sport slug (`sports.js`), per games, where the
 * slug of the sport's NAME is not the portal's. Every other sport's slug is
 * its slugified name (`resultsFeedSportSlug`). The SEA list covers every
 * mismatch among the 43 sports of `SPORT_SEED` (game-settings.data.ts);
 * `tests/unit/public-results-feed.mappers.test.ts` pins all 43. MUA / SIL /
 * WSK keep the portal's slugs after SEAGF's names (Muay Thai, Pencak Silat,
 * Water Skiing; migration 0076).
 */
export const RESULTS_FEED_SPORT_SLUGS: Readonly<Record<ResultsFeedGames, Readonly<Record<string, string>>>> = {
	sea: { RUG: "rugby", BOW: "bowling", BIL: "billiards", MUA: "muay", SIL: "silat", WSK: "water-ski" },
	para: {},
};

/**
 * The para programme's slugs are `p-` + the sport without its "Para "
 * prefix (Para Athletics → p-athletics, Boccia → p-boccia); the one name that
 * then still differs is keyed here by its slugified remainder.
 */
const PARA_NAME_SLUGS: Readonly<Record<string, string>> = { "tenpin-bowling": "bowling" };

/** "Billiards & Snooker" → "billiards-snooker". */
export function resultsFeedSlugify(name: string): string {
	return name
		.trim()
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

/** The portal slug of one sport: the code override, else the (para-prefixed) slug of its name. */
export function resultsFeedSportSlug(games: ResultsFeedGames, code: string, name: string): string {
	const override = RESULTS_FEED_SPORT_SLUGS[games][code];
	if (override) return override;
	if (games === "para") {
		const base = resultsFeedSlugify(name.replace(/^\s*para\s+/i, ""));
		return `p-${PARA_NAME_SLUGS[base] ?? base}`;
	}
	return resultsFeedSlugify(name);
}

/**
 * Our hall's `venues.code` (building level: the parent's code for a court,
 * the code the feed sends as `units[].venue` / `venues[].id`) → the portal's
 * own venue id (`sports.js` VENUES / `games.ts` `sid`), which keys its
 * photos and placeholders. A code not listed passes through unchanged and
 * the portal shows its generic image. Matched by NAME only — a guess by
 * sport would show a Kuching photo for a Bukit Jalil hall.
 * `tests/unit/public-results-feed.mappers.test.ts` keys it to seeded codes.
 */
export const RESULTS_FEED_VENUE_IDS: Readonly<Record<string, string>> = {
	// competition.demo.ts "National Stadium" (Bukit Jalil) = "Stadium Nasional Bukit Jalil".
	NST: "bukit-jalil",
};

/** The portal's venue id for one of our hall codes (the code itself when unmapped). */
export function resultsFeedVenueId(code: string): string {
	return RESULTS_FEED_VENUE_IDS[code] ?? code;
}

/**
 * The longest a unit may plausibly last, in minutes. A scheduled span longer
 * than this (a mistyped end date: one dev football match was scheduled for
 * 26 h 48 m) is ignored and the default duration used; so is an actual end
 * more than this after the unit actually started. A unit that started late
 * keeps its real end — the span is measured from its actual start.
 */
export const RESULTS_FEED_UNIT_MAX_MINUTES = 360;

/** How a ranked event's marks read beside the number: kilograms (lifts), a percentage (dressage). */
export const RESULTS_FEED_UNITS = ["kg", "%"] as const;
/** The most panel marks one judged part carries (seats ≤ 15 per group, two groups for kata / poomsae). */
export const RESULTS_FEED_JUDGES_MAX = 32;

/** The longest lane label a row carries (`meet_competitors.lane` is free text). */
export const RESULTS_FEED_LANE_MAX = 16;
/**
 * The sports whose entrants carry their bib (`bib`) — athletics (user,
 * 2026-11-03: the start list shows it beside the lane). Sport CODES. A bib is
 * printed on the athlete's chest, so it is public by design; every other
 * sport's stays out of the feed until it is listed here.
 */
export const RESULTS_FEED_BIB_SPORTS = ["AT"] as const;
/** An entrant's bib, at most this many characters. */
export const RESULTS_FEED_BIB_MAX = 16;

/** The query: one games, `sea` when omitted. */
export const resultsFeedQuery = z.object({
	games: z.enum(RESULTS_FEED_GAMES).default("sea"),
});
export type ResultsFeedQuery = z.infer<typeof resultsFeedQuery>;

/** `GET /public/results-feed/photos/:id` — an athlete of the feed (their `id`). */
export const resultsFeedPhotoParams = z.object({ id: z.string().uuid() });

/**
 * Which picture: the thumbnail (the portal's faces, the default) or the full
 * TV photo — the venue's Athlete Introduction fills a 780 px portrait with
 * it. Both are the same public TV photo, re-encoded on upload (EXIF stripped).
 */
export const RESULTS_FEED_PHOTO_SIZES = ["thumb", "full"] as const;
export type ResultsFeedPhotoSize = (typeof RESULTS_FEED_PHOTO_SIZES)[number];
export const resultsFeedPhotoQuery = resultsFeedQuery.extend({
	size: z.enum(RESULTS_FEED_PHOTO_SIZES).default("thumb"),
});

// ─── The payload (strict: the tests parse a built feed with these) ───────────

/** Phase codes the portal understands (G QF SF F BR H Q R) plus the round-of-n / repechage / tie-break codes it shows as text. */
export const RESULTS_FEED_PHASES = ["Q", "H", "G", "R", "R128", "R64", "R32", "R16", "QF", "SF", "REP", "BR", "F", "TB"] as const;
export type ResultsFeedPhase = (typeof RESULTS_FEED_PHASES)[number];

export const RESULTS_FEED_UNIT_STATUSES = ["sched", "live", "final"] as const;
export type ResultsFeedUnitStatus = (typeof RESULTS_FEED_UNIT_STATUSES)[number];

/** t time (seconds, lower wins) · n number, lower wins · d distance / weight · p points, higher wins. */
export const RESULTS_FEED_MARK_TYPES = ["t", "n", "d", "p"] as const;
export type ResultsFeedMarkType = (typeof RESULTS_FEED_MARK_TYPES)[number];

/** M men · W women · X mixed · O open. */
export const RESULTS_FEED_EVENT_GENDERS = ["M", "W", "X", "O"] as const;
export const RESULTS_FEED_ENTRANT_TYPES = ["ath", "pair", "team"] as const;
export const RESULTS_FEED_MEDALS = ["G", "S", "B"] as const;
export const RESULTS_FEED_RECORDS = ["GR", "NR"] as const;
/** A row's or a match side's invalid-result mark: OVR's IRM codes, unchanged. */
export const RESULTS_FEED_IRM_CODES = IRM_CODES;

/** The team statistics a match unit's `stats` may hold — every sport's (`TEAM_STAT_CODES_BY_SPORT`); a unit holds only its own sport's, in that sport's order. */
export const RESULTS_FEED_STAT_CODES = TEAM_STAT_CODES;

/** A lift's type on the feed: S snatch · C clean & jerk · B bench press (para powerlifting). */
export const RESULTS_FEED_LIFT_TYPES = { SNATCH: "S", CLEAN_JERK: "C", BENCH: "B" } as const satisfies Record<LiftType, string>;
type ResultsFeedLiftType = (typeof RESULTS_FEED_LIFT_TYPES)[LiftType];
const LIFT_CODES = LIFT_TYPES.map((t) => RESULTS_FEED_LIFT_TYPES[t]) as [ResultsFeedLiftType, ...ResultsFeedLiftType[]];
/**
 * A match unit's `events[].t`: OVR's line-up event types, a substitution as
 * ONE `SUB` (the SUB_OUT and SUB_IN of a side at the same time).
 */
export const RESULTS_FEED_MATCH_EVENTS = ["GOAL", "PENALTY_GOAL", "OWN_GOAL", "YELLOW_CARD", "RED_CARD", "SUB"] as const;
export type ResultsFeedMatchEventType = (typeof RESULTS_FEED_MATCH_EVENTS)[number];

/** A jump / throw row's attempts (`at`): at most this many rounds (ATTEMPTS config `attempts_allowed` ≤ 12). */
export const RESULTS_FEED_ATTEMPTS_MAX = 12;
/** A high jump / pole vault row's heights (`ht`). */
export const RESULTS_FEED_HEIGHTS_MAX = 40;
/** A side's line-up (`lu.a` / `lu.b`): players named for the match. */
export const RESULTS_FEED_LINEUP_MAX = 40;
/** A player's playing position, as the operator typed it ("GK", "Setter"). */
export const RESULTS_FEED_POSITION_MAX = 20;
/** A unit's officials (`off`). */
export const RESULTS_FEED_OFFICIALS_MAX = 40;
/** An official's printed name. */
export const RESULTS_FEED_OFFICIAL_NAME_MAX = 120;
/** An official's role on the feed: OVR's own list. */
export const RESULTS_FEED_OFFICIAL_ROLES = OFFICIAL_ROLES;
/** An attempt that is not a valid mark: X a foul, P passed. */
export const RESULTS_FEED_ATTEMPT_MARKS = ["X", "P"] as const;

const day = z.number().int();
const minute = z.number().int().min(0);
const id = z.string().min(1);
const noc = z.enum(RESULTS_FEED_NOC_CODES);

const games = z
	.object({
		key: z.enum(RESULTS_FEED_GAMES),
		year: z.number().int().optional(),
		month: z.number().int().min(1).max(12).optional(),
		month_name: z.string().optional(),
		mon: z.string().optional(),
		first: day.optional(),
		last: day.optional(),
	})
	.strict();

const venue = z.object({ id, name: z.string(), city: z.string().optional(), cluster: z.string().optional() }).strict();
const sport = z.object({ code: z.string().min(1), name: z.string() }).strict();

const athlete = z
	.object({
		id,
		noc,
		g: z.enum(["M", "W"]).optional(),
		sport: z.string().min(1),
		given: z.string(),
		family: z.string(),
		name: z.string().optional(),
		short: z.string().optional(),
		/**
		 * Has a TV photo (user, 2026-11-02): the portal draws
		 * `GET /public/results-feed/photos/:id?games=` for it, the silhouette
		 * otherwise. Never the accreditation photo, and never a URL here.
		 */
		ph: z.literal(true).optional(),
	})
	.strict();

const entrant = z
	.object({
		id,
		t: z.enum(RESULTS_FEED_ENTRANT_TYPES),
		noc,
		ids: z.array(id).optional(),
		seed: z.number().int().optional(),
		/** The bib — of a `RESULTS_FEED_BIB_SPORTS` sport only: the entry's, else its athlete's in the sport. */
		bib: z.string().min(1).max(RESULTS_FEED_BIB_MAX).optional(),
	})
	.strict();

const group = z.object({ name: z.string().min(1), ids: z.array(id).optional() }).strict();

const points = z.number().int().min(0);
/**
 * How a head-to-head event's matches read (contracts `matchFormatFor`): `per`
 * what one period is (SET, GAME, QUARTER, HALF, END…), `et` / `so` the
 * words for extra play and a shoot-out, `pts` the group-table points — `w`
 * / `d` / `l` a win, draw and loss, `f` a loss by an invalid result, `ew` /
 * `el` a win / loss after extra play or a shoot-out, `sets` points by the
 * match's sets score as `[won, lost, points]` (volleyball 3–2 → 2).
 */
const matchFormat = z
	.object({
		per: z.enum(MATCH_PERIOD_WORDS),
		et: z.enum(MATCH_EXTRA_WORDS).optional(),
		so: z.enum(MATCH_SHOOTOUT_WORDS).optional(),
		/** The extra rows' own names in order (kabaddi's 5R, GR), where they are not "extra time n". */
		xl: z.array(z.string().min(1).max(12)).max(8).optional(),
		/** The group tie-break when it is not the plain score difference: BWF (badminton — contracts `GROUP_TIEBREAKS`). */
		tb: z.enum(["BWF"]).optional(),
		pts: z
			.object({
				w: points,
				d: points,
				l: points,
				f: points.optional(),
				ew: points.optional(),
				el: points.optional(),
				/** An abandoned match, no result (cricket). */
				nr: points.optional(),
				sets: z.array(z.tuple([points, points, points])).optional(),
			})
			.strict()
			.optional(),
	})
	.strict();
const medal = z.object({ m: z.enum(RESULTS_FEED_MEDALS), e: id }).strict();

const event = z
	.object({
		id,
		sport: z.string().min(1),
		label: z.string(),
		g: z.enum(RESULTS_FEED_EVENT_GENDERS).optional(),
		kind: z.enum(["h2h", "rank"]),
		type: z.enum(RESULTS_FEED_MARK_TYPES).optional(),
		dec: z.number().int().min(0).max(3).optional(),
		/** How a ranked mark reads: kilograms (lifts), a percentage (dressage). */
		unit: z.enum(RESULTS_FEED_UNITS).optional(),
		team: z.literal(true).optional(),
		pair: z.literal(true).optional(),
		roster: z.number().int().positive().optional(),
		rounds: z.number().int().positive().optional(),
		heats: z.literal(true).optional(),
		qual: z.literal(true).optional(),
		bronze_match: z.literal(true).optional(),
		/** A head-to-head event: how its matches read and how its group tables count. */
		fmt: matchFormat.optional(),
		/** ROUNDS: what a round is called — `rd` columns read E1 / S1 / R1 / Race 1 / G1… */
		rw: z.enum(ROUND_WORDS).optional(),
		/** A combined event: its units are disciplines, scored on the World Athletics tables. */
		combined: z.enum(COMBINED_KINDS).optional(),
		day: day.optional(),
		medal_at: z.object({ day, min: minute }).strict().optional(),
		medal_units: z.array(id).min(1),
		/** Medals awarded (`medals` non-empty) — the tally's rule; a final awaiting the medal desk is not done. */
		done: z.literal(true).optional(),
		live: z.literal(true).optional(),
		started: z.literal(true).optional(),
		entrants: z.array(entrant).optional(),
		groups: z.array(group).optional(),
		medals: z.array(medal).optional(),
	})
	.strict();

const slotSource = z.union([
	z.object({ u: id, take: z.enum(["W", "L"]) }).strict(),
	z.object({ grp: z.string().min(1), pos: z.number().int().positive() }).strict(),
	z.null(),
]);

/** A split: the elapsed time at it, in seconds (`t`), and its distance in metres (`d`) when recorded. */
const split = z.object({ d: z.number().positive().optional(), t: z.number().min(0) }).strict();

/**
 * One relay leg, in leg order: `p` the athlete (absent when not a sent
 * athlete of the team), `rt` the take-off in seconds — signed: a relay
 * exchange is timed against the incoming swimmer's touch and may legally be
 * early — and `t` the leg's own time in seconds.
 */
const relayLeg = z.object({ p: id.optional(), rt: z.number().optional(), t: z.number().min(0).optional() }).strict();

/**
 * One lift: its type, attempt (1–3), the weight in kg and the verdict — `r`
 * G good lift, N no lift, absent while the judges have not decided.
 */
const lift = z
	.object({
		t: z.enum(LIFT_CODES),
		n: z.number().int().min(1).max(3),
		kg: z.number().positive(),
		r: z.enum(["G", "N"]).optional(),
	})
	.strict();

/**
 * One attempt of a jump / throw row (`at`), at index round − 1 — a round not
 * taken is `{}`, so columns never shift: `v` the mark in metres (a valid
 * attempt), `r` X a foul / P passed, `w` the wind of a valid attempt (m/s).
 */
const attempt = z
	.object({
		v: z.number().min(0).optional(),
		r: z.enum(RESULTS_FEED_ATTEMPT_MARKS).optional(),
		w: z.number().min(-20).max(20).optional(),
	})
	.strict();

/** One height of a high jump / pole vault row (`ht`): `h` the bar in metres, `t` the tries as typed (O cleared, X failed, - passed). */
const heightTries = z
	.object({ h: z.number().positive(), t: z.string().regex(RESULT_HEIGHT_TRIES_PATTERN) })
	.strict();

/** One player of a side's match line-up (`lu`): `p` the athlete, `st` started, `c` captain, `pos` the playing position, `n` the line-up order, `sn` the shirt number. */
const lineupPlayer = z
	.object({
		p: id,
		st: z.literal(true).optional(),
		c: z.literal(true).optional(),
		pos: z.string().min(1).max(RESULTS_FEED_POSITION_MAX).optional(),
		n: z.number().int().min(0).optional(),
		/** The shirt (cap) number — worn on the back, so public (user, 2026-11-03): the roster's, else the athlete's in the sport. */
		sn: z.string().min(1).max(RESULTS_FEED_BIB_MAX).optional(),
	})
	.strict();

/** One official of a unit (`off`): `r` the role, `n` the printed name, `pos` the seat ("J1", "FX:E1"), `e` the entrant a coach / manager is with. */
const official = z
	.object({
		r: z.enum(RESULTS_FEED_OFFICIAL_ROLES),
		n: z.string().min(1).max(RESULTS_FEED_OFFICIAL_NAME_MAX),
		pos: z.string().min(1).max(RESULTS_FEED_POSITION_MAX).optional(),
		e: id.optional(),
	})
	.strict();

/**
 * A judged part (JUDGED detail `parts`), as the result sheet prints it: `c`
 * the part (an apparatus, a dance, a segment, a round), `l` its name, `d`
 * the difficulty (TES), `x` the components (`k` its code: median,
 * a_quality, technical, pcs…), `p` the penalty, `t` the part total, `rk`
 * the place in that part, `dr` not counted (a team's worst rider), `j` the
 * panel's marks (`s` the seat, `dr` a mark dropped high / low).
 */
const judgedPart = z
	.object({
		c: z.string().min(1).max(12),
		l: z.string().min(1).max(60).optional(),
		d: z.number().optional(),
		x: z.array(z.object({ k: z.string().min(1).max(30), v: z.number() }).strict()).max(16).optional(),
		p: z.number().optional(),
		t: z.number(),
		rk: z.number().int().positive().optional(),
		dr: z.literal(true).optional(),
		j: z.array(z.object({ s: z.string().min(1).max(8), v: z.number(), dr: z.literal(true).optional() }).strict()).max(RESULTS_FEED_JUDGES_MAX).optional(),
	})
	.strict();

/**
 * One round of a ROUNDS row (`rd`), at index seq − 1 — a round not shot is `{}`,
 * so columns never shift: `v` its value in the event's unit (absent: none),
 * `d` 1 = discarded (shown in brackets), `irm` the round's own mark (a sailing
 * OCS still scores `v`), `p` the points it earned (archery set points, a
 * bowling bonus), `l` its label ("70m-1", "K1", "MR", "JO"), `x` 2 = it
 * counts double (a medal race), `jo` 1 = a decider (jump-off, play-off — never
 * in the total), `h` its parts (holes, ends, shots, frames) in order.
 */
const roundCell = z
	.object({
		v: z.number().optional(),
		d: z.literal(1).optional(),
		irm: z.enum(RESULTS_FEED_IRM_CODES).optional(),
		p: z.number().optional(),
		l: z.string().min(1).max(ROUND_LABEL_MAX).optional(),
		x: z.literal(2).optional(),
		jo: z.literal(1).optional(),
		h: z.array(z.number()).max(ROUND_PARTS_MAX).optional(),
	})
	.strict();

/**
 * One member of a ROUNDS team row with their OWN rounds (a bowling team's
 * bowler and their games), in line-up order: `p` the athlete (absent when
 * not a sent athlete of the team), `v` their total, `rd` their rounds
 * (`roundCell`, values only — no parts, no marks).
 */
const memberRounds = z.object({ p: id.optional(), v: z.number().optional(), rd: z.array(roundCell).max(40) }).strict();

/** A ROUNDS row's sheet counts: `t` 10s (X included), `x` Xs / inner tens, `g` the total before discards (sailing). */
const tallyCells = z.object({ t: z.number().optional(), x: z.number().optional(), g: z.number().optional() }).strict();

const row = z
	.object({
		e: id,
		v: z.number().optional(),
		rank: z.number().int().positive().optional(),
		/** An invalid result: no `rank`, no `v`; listed after the ranked rows. */
		irm: z.enum(RESULTS_FEED_IRM_CODES).optional(),
		/** ROUNDS: the round values only (kept beside `rd` for one release). */
		splits: z.array(z.number()).optional(),
		/** ROUNDS: every round, padded by seq (see `roundCell`). */
		rd: z.array(roundCell).max(40).optional(),
		/** ROUNDS: the sheet's counts (10+X, X, gross). */
		tl: tallyCells.optional(),
		/** ROUNDS, a team row: each member's own rounds (a bowling team's bowlers), in line-up order. */
		mr: z.array(memberRounds).max(8).optional(),
		/** Golf: to par over the holes played (0 = E). */
		tp: z.number().int().optional(),
		/** Golf: holes played in the latest round; absent = finished. */
		th: z.number().int().min(0).max(ROUND_PARTS_MAX).optional(),
		/** A placed race (cycling, short track): laps gained (+) / lost (−) — "-1 LAP". */
		ld: z.number().int().optional(),
		/** A placed race: the same time as the rider placed just ahead ("s.t."). */
		st: z.literal(true).optional(),
		q: z.enum(["Q", "q"]).optional(),
		rec: z.enum(RESULTS_FEED_RECORDS).optional(),
		/**
		 * Combined events (then `v` is the running total, `rank` the overall place): the discipline's points…
		 * A SERIES unit (`unit.series`: golf rounds, sailing races, bowling squads as separate
		 * meets) reads the same way: `v` the series total after this meet, `rank` the overall
		 * place, `m` this meet's own value, `dr` the place in this meet, `rd` every meet so far.
		 */
		pts: z.number().int().min(0).optional(),
		/** …the discipline's mark, in the unit's `type` / `dec`… */
		m: z.number().optional(),
		/** …and the place in the discipline. */
		dr: z.number().int().positive().optional(),
		/** …and the discipline's own invalid result (NM, DNF, DSQ): no points there; `v` / `rank` (the standing) stay. */
		di: z.enum(RESULTS_FEED_IRM_CODES).optional(),
		/** A race: the reaction time, in seconds (0.145). */
		rt: z.number().min(0).optional(),
		/** A jump or throw: the wind of the best mark, m/s… */
		w: z.number().min(-20).max(20).optional(),
		/** …and that wind made the mark wind-assisted (above `WIND_ASSIST_LIMIT_MPS`): a result, never a record. */
		wa: z.literal(true).optional(),
		/** A jump or throw: every attempt, by round (see `attempt`). */
		at: z.array(attempt).max(RESULTS_FEED_ATTEMPTS_MAX).optional(),
		/** A high jump or pole vault: every height attempted, in bar order, with its tries. */
		ht: z.array(heightTries).max(RESULTS_FEED_HEIGHTS_MAX).optional(),
		/** Weightlifting: every lift taken, in lifting order. */
		lifts: z.array(lift).max(9).optional(),
		/** A race: the splits, in race order (the finish is `v`). */
		sp: z.array(split).max(64).optional(),
		/** A relay: its legs, in leg order. */
		legs: z.array(relayLeg).max(8).optional(),
		/** The lane (or position) the operator set for this competitor, as typed ("3"). */
		ln: z.string().min(1).max(RESULTS_FEED_LANE_MAX).optional(),
		/** A start list: the competitor's start order (the performance order of a judged round); a LIVE jump / throw keeps it on every row (the order of attempts). */
		so: z.number().int().positive().optional(),
		/** The bib typed for this meet on OVR's start list — of a `RESULTS_FEED_BIB_SPORTS` sport only; it wins over the entrant's `bib`. */
		bib: z.string().min(1).max(RESULTS_FEED_BIB_MAX).optional(),
		/** A judged result: its parts, as the panel scored them (the result is `v`). */
		jp: z.array(judgedPart).max(JUDGED_PARTS_MAX).optional(),
	})
	.strict();

/** One team statistic of a match: home (`a`) and away (`b`); a side with no value has none. */
const matchStat = z
	.object({ k: z.enum(RESULTS_FEED_STAT_CODES), a: z.number().int().min(0).optional(), b: z.number().int().min(0).optional() })
	.strict();

/** One set's team statistics (`p` = the set, 1-based), the same shape as the whole match's. */
const setStat = z.object({ p: z.number().int().min(1).max(SETS_BEST_OF_MAX), s: z.array(matchStat).min(1) }).strict();

/**
 * One match event. `s` is the side of the PLAYER (an own goal by side `a`
 * counts for `b`); `min` / `add` / `per` the minute, stoppage time (45+2' is
 * 45 and 2) and period, when recorded; `p` the player (for `SUB`, the one
 * coming on) and `off` the one going off — each an athlete id, absent when
 * the player is not a sent athlete or that half of a substitution is missing.
 */
const matchEvent = z
	.object({
		s: z.enum(["a", "b"]),
		t: z.enum(RESULTS_FEED_MATCH_EVENTS),
		min: z.number().int().min(0).optional(),
		add: z.number().int().min(1).max(MATCH_EVENT_ADDED_MAX).optional(),
		per: z.number().int().positive().optional(),
		p: id.optional(),
		off: id.optional(),
	})
	.strict();

// ── BOUT (combat sports): what the operator entered beyond the score ─────────
const count = z.number().int().min(0);
/**
 * A bout's scoresheet, beside `res.a` / `res.b` (the headline the decision
 * reads: rounds won, judges' votes, points, or judo's ladder figure) and
 * `res.sets` (the rounds' scores). `dec` how the headline is counted
 * (contracts `BOUT_DECISIONS`); `pw` what the penalty count is called;
 * `pen` each round's penalty count `[a, b]` (null = none entered), in the
 * order of the rounds; `pt` the bout's penalty totals; `rw` each round's
 * winner and how (`[side, code, label]`, a level round decided on
 * tie-break included); `cards` the judges' cards — `s` the seat ("J1"),
 * `a` / `b` the round scores, `ta` / `tb` the totals; `tl` the tallies —
 * `k` the code, `l` its printed name; `legs` a fencing team relay's fencer
 * per leg `[a, b]` (athlete ids, null when not a sent athlete).
 */
const boutSheet = z
	.object({
		dec: z.enum(BOUT_DECISIONS),
		pw: z.string().optional(),
		pen: z.array(z.tuple([count.nullable(), count.nullable()])).optional(),
		pt: z.tuple([count.nullable(), count.nullable()]).optional(),
		rw: z.array(z.tuple([z.enum(["a", "b"]).nullable(), z.string().nullable(), z.string().nullable()])).optional(),
		cards: z
			.array(
				z
					.object({
						s: z.string().min(1),
						a: z.array(z.number()),
						b: z.array(z.number()),
						ta: z.number().nullable(),
						tb: z.number().nullable(),
					})
					.strict(),
			)
			.optional(),
		tl: z.array(z.object({ k: z.string().min(1), l: z.string(), a: z.number().nullable(), b: z.number().nullable() }).strict()).optional(),
		legs: z.array(z.tuple([id.nullable(), id.nullable()])).optional(),
	})
	.strict();

/** A head-to-head's score — a match's, or one rubber's inside a tie. */
const h2hRes = z
	.object({
		w: z.enum(["a", "b"]).optional(),
		/** The winner `w` went through on a bye: there was no opponent, and the other side is empty — draw it "Bye", never "To be decided". */
		bye: z.literal(true).optional(),
		a: z.number().optional(),
		b: z.number().optional(),
		sets: z.array(z.tuple([z.number(), z.number()])).optional(),
		/** Tie-break points, set by set beside `sets` (tennis 7–6⁽⁵⁾): `[a, b]`, null for a side with none; present only when a set had one. */
		tb: z.array(z.tuple([z.number().int().min(0).nullable(), z.number().int().min(0).nullable()])).optional(),
		method: z.string().optional(),
		/** `method`'s printed name ("Referee stops contest"), contracts `winMethodLabel`. */
		ml: z.string().optional(),
		/**
		 * The match is played in TIMED periods (football's halves): `sets` are
		 * the periods' scores, not games won — `a` / `b` are the goals.
		 */
		per: z.literal(true).optional(),
		/** Extra time, period by period (`[a, b]`), when played. */
		et: z.array(z.tuple([z.number().int().min(0), z.number().int().min(0)])).optional(),
		/** A penalty shoot-out's score (`[a, b]`), when there was one; it decides `w`, never `a` / `b`. */
		so: z.tuple([z.number().int().min(0), z.number().int().min(0)]).optional(),
		/**
		 * Cricket: the innings in match order — `s` whose (a / b), `r` runs, `w`
		 * wickets, `b` balls faced (overs.balls on the page), `t` a revised
		 * target, `so` a super-over innings.
		 */
		inn: z
			.array(
				z
					.object({
						s: z.enum(["a", "b"]),
						r: z.number().int().min(0),
						w: z.number().int().min(0).max(CRICKET_WICKETS_MAX),
						b: z.number().int().min(0),
						t: z.number().int().min(1).optional(),
						so: z.literal(true).optional(),
					})
					.strict(),
			)
			.optional(),
		/** Cricket: how the winner won — by `n` runs, by `n` wickets (`bl` balls left), the super over, or the tie-break after level super overs. */
		by: z
			.object({ u: z.enum(["runs", "wickets", "super_over", "tie_break"]), n: z.number().int().min(0).optional(), bl: z.number().int().min(0).optional() })
			.strict()
			.optional(),
		/** Cricket: abandoned, no result. */
		nr: z.literal(true).optional(),
		/** A judged bout (kata, breaking, seni): each side's parts with the panel's marks; `a` / `b` are votes or rounds won. */
		j: z.object({ a: z.array(judgedPart).max(JUDGED_PARTS_MAX).optional(), b: z.array(judgedPart).max(JUDGED_PARTS_MAX).optional() }).strict().optional(),
		/** Archery: the shoot-off went to the arrow closest to the centre — this side's. */
		soc: z.enum(["a", "b"]).optional(),
		/** Archery set play: `a` / `b` are set points, `sets` the end totals. */
		sp: z.literal(true).optional(),
		/** Cumulative scoring (compound archery, a bowling match): `a` / `b` are the totals of `sets`. */
		sum: z.literal(true).optional(),
		/** A bout (BOUT): its scoresheet — cards, penalties, rounds' decisions, tallies, relay legs. */
		bout: boutSheet.optional(),
		/** A side's invalid-result mark (RET retired, WO walkover, DSQ…); the score is as it stood. */
		irm: z
			.object({ a: z.enum(RESULTS_FEED_IRM_CODES).optional(), b: z.enum(RESULTS_FEED_IRM_CODES).optional() })
			.strict()
			.optional(),
	})
	.strict();

const unit = z
	.object({
		id,
		ev: id,
		/**
		 * This unit reads differently from its event (`event.kind`), decided by
		 * the sport's rules, never by a stored outcome: a judged bout by its
		 * scoring rule (karate kata medal bouts, breaking — contracts
		 * `judgedIsBout`), an archery or cycling sprint match in a ranked event
		 * (contracts `isMatchUnit`) goes out `h2h` (`a` / `b` / `res`, no rows).
		 * Absent = the event's kind.
		 */
		kind: z.enum(["h2h", "rank"]).optional(),
		phase: z.enum(RESULTS_FEED_PHASES),
		label: z.string(),
		day,
		start: minute,
		dur: z.number().int().positive(),
		end: minute,
		venue: z.string().optional(),
		area: z.string().optional(),
		group: z.string().optional(),
		/** The rows are the series standing after this meet (see `row.m`). */
		series: z.literal(true).optional(),
		/**
		 * How the unit's marks read when not as the event's: a combined-event discipline's
		 * own marks (`rows[].m`), a points race (`p`) inside a timed event; `dec` its decimals.
		 */
		type: z.enum(RESULTS_FEED_MARK_TYPES).optional(),
		dec: z.number().int().min(0).max(3).optional(),
		medal: z.literal(true).optional(),
		status: z.enum(RESULTS_FEED_UNIT_STATUSES).optional(),
		/** A `final` unit OVR has not made official yet (the meet is UNOFFICIAL): the portal shows "Unofficial". */
		uo: z.literal(true).optional(),
		a: id.optional(),
		b: id.optional(),
		src: z.tuple([slotSource, slotSource]).optional(),
		res: h2hRes.optional(),
		/**
		 * A team tie: its rubbers (the child meets), in playing order — `n` the
		 * rubber's place, `l` its name ("Singles 1"), `st` its status, `a` /
		 * `b` the players of the tie's side a / b (athlete ids), `res` its score
		 * as a match's, sides aligned with the TIE's.
		 */
		rub: z
			.array(
				z
					.object({
						id,
						n: z.number().int().min(1),
						l: z.string(),
						st: z.enum(RESULTS_FEED_UNIT_STATUSES),
						a: z.array(id).optional(),
						b: z.array(id).optional(),
						res: h2hRes.optional(),
					})
					.strict(),
			)
			.optional(),
		rows: z.array(row).optional(),
		/** A match (SETS): the whole-match team statistics. */
		stats: z.array(matchStat).optional(),
		/** A match of a sport kept per set (`TEAM_STATS_BY_SET_SPORTS`: sepak takraw): each set's team statistics, in set order. */
		sst: z.array(setStat).optional(),
		/** A match (SETS): goals, cards and substitutions, in match order. */
		events: z.array(matchEvent).optional(),
		/** A team match (SETS): each side's line-up, starters first, then in line-up order. */
		lu: z
			.object({ a: z.array(lineupPlayer).max(RESULTS_FEED_LINEUP_MAX).optional(), b: z.array(lineupPlayer).max(RESULTS_FEED_LINEUP_MAX).optional() })
			.strict()
			.optional(),
		/** The unit's officials — neutral ones first, by OVR's order, then each entrant's coach / manager. */
		off: z.array(official).max(RESULTS_FEED_OFFICIALS_MAX).optional(),
		/** A match (SETS): how long each game lasted — `[game, minutes]`, in game order, the games recorded only. */
		gdur: z.array(z.tuple([z.number().int().positive(), z.number().int().min(0)])).optional(),
		/** A match (SETS): the match duration in minutes, as recorded (the games plus the intervals). */
		mdur: z.number().int().min(0).optional(),
		/** A race (TIMED): the wind reading, m/s… */
		wind: z.number().min(-20).max(20).optional(),
		/** …and it made the race's marks wind-assisted. */
		wa: z.literal(true).optional(),
		/** The meet's video (0086): the embed URL, its platform; `featured` sorts it first (absent = not featured). */
		video: z
			.object({ src: z.string().url(), platform: z.enum(STREAM_PLATFORMS), featured: z.literal(true).optional() })
			.strict()
			.optional(),
	})
	.strict();

/** The games' featured stream while it is switched on (0086): the embed URL, its platform, its title. */
const featuredVideo = z.object({ src: z.string().url(), platform: z.enum(STREAM_PLATFORMS), title: z.string().min(1) }).strict();

const record = z
	.object({ type: z.enum(RESULTS_FEED_RECORDS), u: id, ev: id, e: id, v: z.number(), day, min: minute })
	.strict();

export const resultsFeedSchema = z
	.object({
		games,
		now: z.object({ day, min: minute }).strict(),
		nocs: z.array(z.object({ code: noc }).strict()),
		venues: z.array(venue),
		sports: z.array(sport),
		athletes: z.array(athlete),
		events: z.array(event),
		units: z.array(unit),
		records: z.array(record),
		featured_video: featuredVideo.nullable(),
	})
	.strict();

export type ResultsFeed = z.infer<typeof resultsFeedSchema>;
export type ResultsFeedEvent = z.infer<typeof event>;
export type ResultsFeedUnit = z.infer<typeof unit>;
export type ResultsFeedH2hRes = z.infer<typeof h2hRes>;
export type ResultsFeedRubber = NonNullable<ResultsFeedUnit["rub"]>[number];
export type ResultsFeedUnitRow = z.infer<typeof row>;
export type ResultsFeedEntrant = z.infer<typeof entrant>;
export type ResultsFeedAthlete = z.infer<typeof athlete>;
export type ResultsFeedSlotSource = z.infer<typeof slotSource>;
export type ResultsFeedRecord = z.infer<typeof record>;
export type ResultsFeedFeaturedVideo = z.infer<typeof featuredVideo>;
export type ResultsFeedMatchStat = z.infer<typeof matchStat>;
export type ResultsFeedSetStat = z.infer<typeof setStat>;
export type ResultsFeedMatchEvent = z.infer<typeof matchEvent>;
export type ResultsFeedLift = z.infer<typeof lift>;
export type ResultsFeedSplit = z.infer<typeof split>;
export type ResultsFeedRelayLeg = z.infer<typeof relayLeg>;
export type ResultsFeedMemberRounds = z.infer<typeof memberRounds>;
export type ResultsFeedBoutSheet = z.infer<typeof boutSheet>;
export type ResultsFeedJudgedPart = z.infer<typeof judgedPart>;
export type ResultsFeedAttempt = z.infer<typeof attempt>;
export type ResultsFeedHeightTries = z.infer<typeof heightTries>;
export type ResultsFeedLineupPlayer = z.infer<typeof lineupPlayer>;
export type ResultsFeedOfficial = z.infer<typeof official>;
